import debounce from "awesome-debounce-promise";
import { clsx } from "clsx";
import { createRef, PureComponent, type ReactElement } from "react";
import type { FormatOptionLabelMeta } from "react-select";
import Select from "react-select";
import Async from "react-select/async";
import type { AsyncProps } from "react-select/dist/declarations/src/useAsync.js";

import { type GenomeSearch, type Organism } from "@/graphql.ts";
import { UNKNOWN_GENOME_ID } from "@/settings/settings.ts";

export interface IGenomeSelectorProps {
  selectedGenome: Organism;
  classNames?: string;
  onSearch: (searchTerm: string) => Promise<GenomeSearch>; // callback invoked when the user types in the react-select field
  onSelect: (genome: Organism) => void; // callback sending the selected genome ID to the viewer
  onFocus?: (genome: Organism) => void;
  genomes?: Organism[]; // to show only the list of mutant genomes in the genome picker e.g. for Variant's genome browser
  selectProps?: Partial<AsyncProps<any, any, any>>;
  additionalInformation?: string;
}

class GenomeSelector extends PureComponent<IGenomeSelectorProps> {
  static DEBOUNCE_TIMER = 300;
  inputRef = createRef<HTMLInputElement>();

  constructor(props: IGenomeSelectorProps) {
    super(props);

    this.handleSelectedItem = this.handleSelectedItem.bind(this);

    // SEL6-2681: for debouncing Promises, use awesome-debounce-promise library, together with onlyResolvesLast option to support the react-select's cache feature
    this.loadOptions = debounce(this.loadOptions, GenomeSelector.DEBOUNCE_TIMER, { onlyResolvesLast: true });
  }

  componentDidMount() {
    if (!this.props.selectedGenome) {
      // Force focus because the main menu can take it
      this.inputRef.current.focus();
    }
  }

  static selectOptionFormatter(
    option: Organism,
    labelMeta: FormatOptionLabelMeta<Organism>,
    additionalInfo?: string,
  ): ReactElement {
    const isSelection: boolean = labelMeta.context === "value";
    return isSelection ? (
      <div key="select-selection">
        <div
          className="main-label"
          style={{
            overflow: "hidden",
            textOverflow: "ellipsis" /*, width: '90%'*/,
          }}
        >
          {`${option.name}${additionalInfo ?? ""}`}
        </div>
      </div>
    ) : (
      <div key={"select-option-" + option.id}>
        <span className="main-label">{option.id === UNKNOWN_GENOME_ID ? `Sequences` : `${option.name}`}</span>{" "}
        {option.species && (
          <span className="secondary" style={{ fontStyle: "italic" }}>
            ({option.species.name})
          </span>
        )}
      </div>
    );
  }

  render(): ReactElement {
    const selectDisabled = this.props.genomes && this.props.genomes.length === 1;
    const selectClassNames = clsx("react-select genome-selector", this.props.classNames);

    const defaultSelectProps: AsyncProps<Organism, any, any> = {
      className: selectClassNames,
      classNamePrefix: "react-select",
      name: "genome-selector",
      placeholder: "Select a genome...",
      isSearchable: false,
      getOptionValue: (option: Organism) => option.id,
      value: this.props.selectedGenome,
      isDisabled: selectDisabled,

      onChange: this.handleSelectedItem,
      onFocus: this.props.onFocus,
      noOptionsMessage: () => "No matching genome...",
      formatOptionLabel: (option: Organism, labelMeta: FormatOptionLabelMeta<Organism>) =>
        GenomeSelector.selectOptionFormatter(option, labelMeta, this.props.additionalInformation),

      menuPortalTarget: document.body,
      styles: {
        menuPortal: (base) => ({
          ...base,
          zIndex: 650,
        }),
        menu: (styles) => ({ ...styles, width: "max-content", minWidth: "100%" }),
      },
    };

    return (
      <div>
        {this.props.genomes ? (
          <Select
            {...defaultSelectProps}
            options={this.props.genomes}
            {...this.props.selectProps}
            ref={this.inputRef}
          />
        ) : (
          <Async
            {...defaultSelectProps}
            loadOptions={this.loadOptions}
            cacheOptions={true}
            isSearchable
            defaultOptions={true} // tells the control to immediately fire the remote request
            autoFocus={!this.props.selectedGenome}
            openMenuOnFocus={!this.props.selectedGenome}
            {...this.props.selectProps}
            ref={this.inputRef}
          />
        )}
      </div>
    );
  }

  loadOptions = (searchTerm: string) => {
    return this.props.onSearch(searchTerm).then((genomeSearch: GenomeSearch) => {
      const selectedGenome = this.props.selectedGenome;
      const genomes = genomeSearch.genomes?.slice(0);
      if (!searchTerm && selectedGenome && !genomes?.find((genome: Organism) => genome.id === selectedGenome.id)) {
        genomes?.unshift(selectedGenome);
      }
      return genomes;
    });
  };

  handleSelectedItem = (selected: Organism) => {
    // if we support clearing the selection, we need to allow a null value to be passed on
    if (selected?.id || this.props.selectProps?.isClearable) {
      // when pressing Delete key after focusing on the field, we get an empty array (?)
      this.props.onSelect(selected);
    }
  };
}

export { GenomeSelector };
